import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
// @mui
import {
  Card,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// shared components
import ClearButton from 'src/components/shared/ClearButton';
import FilterButton from 'src/components/shared/FilterButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import EditButton from 'src/components/shared/EditButton';
import CustomFilter from 'src/components/filter/CustomFilter';
import api from 'src/services/BaseService';
import { useLocalization } from 'src/context/LocalizationContext';

export default function EasyMallList() {
  const { language } = useLocalization();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');

  // Data states
  const [originalData, setOriginalData] = useState([]);
  const [storeList, setStoreList] = useState([]);

  // Filter states
  const [branchList, setBranchList] = useState([]);
  const [mallList, setMallList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedMall, setSelectedMall] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterModal, setFilterModal] = useState(false);

  // Table headers
  const STORE_TABLE_HEAD = [
    {
      id: 'title',
      label: 'Mağaza',
      format: (_, row) => row.title,
    },
    {
      id: 'mallTitle',
      label: 'AVM',
      format: (_, row) => row.mallTitle,
    },
    {
      id: 'concierge',
      label: 'Konsiyerj',
      format: (_, row) =>
        row.easyMallSettings.conciergeStatus.status ? 'Var' : 'Yok',
    },
    {
      id: 'system',
      label: 'Sistem',
      format: (_, row) =>
        row.easyMallSettings.systemStatus.status ? 'Var' : 'Yok',
    },
    {
      id: 'conciergeDate',
      label: 'Konsiyerj Kabul Tarihi',
      format: (_, row) =>
        moment
          .unix(row.easyMallSettings.conciergeStatus.agreementAcceptDateTime)
          .format('DD.MM.YYYY HH:mm'),
    },
    {
      id: 'systemDate',
      label: 'Sistem Kabul Tarihi',
      format: (_, row) =>
        moment
          .unix(row.easyMallSettings.systemStatus.agreementAcceptDateTime)
          .format('DD.MM.YYYY HH:mm'),
    },
  ];

  // Fetch list and build filter options
  const getEasyMallList = () => {
    api
      .post('/flow/get-allowed-easy-mall-branches')
      .then((res) => {
        if (!res.data.status) {
          setOriginalData([]);
          setStoreList([]);
          setMallList([]);
          setBranchList([]);
          return;
        }
        const data = res.data.result;
        setOriginalData(data);
        setStoreList(data);

        // Branch list for filter
        setBranchList(
          data?.map((it) => ({ id: it.branchID, label: it.title }))
        );

        // Mall list for filter (unique)
        const malls = [];
        data?.forEach((it) => {
          if (!malls?.find((m) => m.id === it.mallID)) {
            malls.push({ id: it.mallID, label: it.mallTitle });
          }
        });
        setMallList(malls);
      })
      .catch(() => {
        setOriginalData([]);
        setStoreList([]);
        setMallList([]);
        setBranchList([]);
      });
  };

  useEffect(() => {
    getEasyMallList();
  }, []);

  // Apply filters
  const getFilteredStoreList = () => {
    const filtered = originalData?.filter((item) => {
      const byBranch = selectedBranch ? item.branchID === selectedBranch : true;
      const byMall = selectedMall ? item.mallID === selectedMall : true;
      const byDate =
        startDate && endDate
          ? moment
              .unix(item.easyMallSettings.systemStatus.agreementAcceptDateTime)
              .isBetween(
                startDate.startOf('day'),
                endDate.endOf('day'),
                undefined,
                '[]'
              )
          : true;
      return byBranch && byMall && byDate;
    });
    setStoreList(filtered);
  };

  const clearFilters = () => {
    setSelectedBranch('');
    setSelectedMall('');
    setStartDate(null);
    setEndDate(null);
    setStoreList(originalData);
  };

  return (
    <>
      <Helmet>
        <title>Easy Mall Listesi | Mall Logistics Panel</title>
      </Helmet>

      <Stack spacing={4} sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h4">Easy Mall Listesi</Typography>
          {isMobile && (
            <Button variant="outlined" onClick={() => setFilterModal(true)}>
              Filtrele
            </Button>
          )}
        </Stack>

        <Card>
          <CustomFilter
            modalType="drawer"
            filterModal={filterModal}
            handleCloseModal={() => setFilterModal(false)}
          >
            <Stack spacing={2} sx={{ p: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Mağaza</InputLabel>
                <Select
                  value={selectedBranch}
                  label="Mağaza"
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Hepsi</em>
                  </MenuItem>
                  {branchList.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>AVM</InputLabel>
                <Select
                  value={selectedMall}
                  label="AVM"
                  onChange={(e) => setSelectedMall(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Hepsi</em>
                  </MenuItem>
                  {mallList.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Başlangıç Tarihi"
                  value={startDate}
                  onChange={setStartDate}
                  format="DD/MM/YYYY"
                />
                <DatePicker
                  label="Bitiş Tarihi"
                  value={endDate}
                  onChange={setEndDate}
                  format="DD/MM/YYYY"
                />
              </LocalizationProvider>

              <Divider />

              <Stack direction="row" spacing={1} justifyContent="center">
                <ClearButton onClick={clearFilters} />
                <FilterButton onClick={getFilteredStoreList} />
              </Stack>
            </Stack>
          </CustomFilter>

          <DynamicTable
            data={storeList}
            headers={STORE_TABLE_HEAD}
            isMobile={isMobile}
            isOrdered={true}
            handleModal={() => setFilterModal(true)}
            handleCloseModal={() => setFilterModal(false)}
          />
        </Card>
      </Stack>
    </>
  );
}
